import { apiClient } from '../api/apiClient';
import { API_ENDPOINTS } from '../api/apiEndpoints';
import type {
  WorkflowDetails,
  WorkflowHistoryItem,
  WorkflowInboxFilters,
  WorkflowInboxItem,
  WorkflowInboxResult,
} from '../types/workflow';
import {
  createDefaultWorkflowFilters,
  normalizeWorkflowFilters,
} from '../utils/workflowFilters';
import { getWorkflowRequestTypeLabel } from '../utils/workflowRequestType';
import {
  getWorkflowActionLabel,
  getWorkflowStatusLabel,
} from '../utils/workflowStatus';

type Envelope<T> = { success: boolean; message: string; data: T };
type ObjectValue = Record<string, unknown>;

function numberOrNull(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function textOrNull(value: unknown): string | null {
  if (typeof value === 'string' && value.trim()) return value.trim();
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  return null;
}

function reasonSummary(value: unknown): string | null {
  const text = textOrNull(value);
  if (!text) return null;
  return text.length > 80 ? `${text.slice(0, 77)}...` : text;
}

function requestTypeCode(row: ObjectValue): string | null {
  return textOrNull(
    row.entityType ?? row.requestTypeCode ?? row.requestType ?? row.entityCode,
  );
}

function camelizeRow(row: ObjectValue): ObjectValue {
  const flattened: ObjectValue = { ...row };
  for (const [key, value] of Object.entries(row)) {
    flattened[toCamelKey(key)] = value;
  }
  return flattened;
}

function requestReferenceId(row: ObjectValue): number | null {
  // Prefer module-specific ids before generic requestId, which can collide
  // with workflow_instance_id or other surrogate keys on some payloads.
  return numberOrNull(
    row.correctionRequestId ??
      row.attendanceCorrectionRequestId ??
      row.wfhRequestId ??
      row.leaveApplicationId ??
      row.permissionRequestId ??
      row.requestReferenceId ??
      row.entityId ??
      row.requestId,
  );
}

function normalizeInboxItem(row: ObjectValue): WorkflowInboxItem | null {
  const item = camelizeRow(row);
  const workflowInstanceId = numberOrNull(
    item.workflowInstanceId ?? item.instanceId,
  );
  if (workflowInstanceId === null || workflowInstanceId <= 0) return null;

  const typeCode = requestTypeCode(item);
  const statusCode =
    textOrNull(
      item.workflowStatus ?? item.workflowStatusCode ?? item.statusCode,
    ) ??
    textOrNull(item.status) ??
    null;

  return {
    workflowInstanceId,
    requestTypeCode: typeCode,
    requestTypeLabel: getWorkflowRequestTypeLabel(typeCode),
    requestReferenceId: requestReferenceId(item),
    requestNumber: textOrNull(
      item.requestNumber ?? item.entityNumber ?? item.referenceNumber,
    ),
    employee: {
      employeeId: numberOrNull(item.employeeId ?? item.requesterEmployeeId),
      employeeNumber: textOrNull(
        item.employeeNumber ?? item.employeeCode ?? item.requesterEmployeeNumber,
      ),
      displayName: textOrNull(
        item.employeeName ??
          item.displayName ??
          item.employeeDisplayName ??
          item.requesterName,
      ),
    },
    submittedAt: textOrNull(
      item.submittedAt ??
        item.appliedAt ??
        item.appliedOn ??
        item.createdAt,
    ),
    currentStepName: textOrNull(
      item.currentStepName ??
        item.stepName ??
        (item.currentStepNumber != null
          ? `Step ${item.currentStepNumber}`
          : null),
    ),
    workflowStatusCode: statusCode,
    workflowStatusLabel: getWorkflowStatusLabel(statusCode),
    requestSummary: reasonSummary(
      item.requestSummary ?? item.summary ?? item.reason,
    ),
    rejectReason: textOrNull(
      item.rejectReason ?? item.rejectionReason ?? item.rejectedReason,
    ),
    cancellationReason: textOrNull(
      item.cancellationReason ??
        item.cancelReason ??
        item.cancelledReason ??
        item.canceledReason,
    ),
    // Generic approve/reject return 409; never enable from the client.
    canApprove: false,
    canReject: false,
  };
}

function toCamelKey(key: string): string {
  return key.replace(/_([a-z0-9])/gi, (_, ch: string) => ch.toUpperCase());
}

function flattenWorkflowPayload(row: ObjectValue): ObjectValue {
  const nested = row.requestDetails;
  const sources: ObjectValue[] = [row];
  if (nested && typeof nested === 'object' && !Array.isArray(nested)) {
    sources.push(nested as ObjectValue);
  }
  const flattened: ObjectValue = {};
  for (const source of sources) {
    for (const [key, value] of Object.entries(source)) {
      if (key === 'requestDetails') continue;
      flattened[key] = value;
      flattened[toCamelKey(key)] = value;
    }
  }
  return flattened;
}

function firstPresent(...values: unknown[]): unknown {
  for (const value of values) {
    if (value !== undefined && value !== null && value !== '') return value;
  }
  return undefined;
}

function pickRequestDetails(row: ObjectValue): Record<string, unknown> | null {
  const src = flattenWorkflowPayload(row);
  const details: Record<string, unknown> = {};
  const assign = (key: string, value: unknown) => {
    if (value !== undefined && value !== null && value !== '') {
      details[key] = value;
    }
  };

  assign(
    'leaveTypeName',
    firstPresent(src.leaveTypeName, src.leaveName),
  );
  assign(
    'leaveFromDate',
    firstPresent(src.leaveFromDate, src.fromDate, src.startDate),
  );
  assign(
    'leaveToDate',
    firstPresent(src.leaveToDate, src.toDate, src.endDate),
  );
  assign(
    'fromDate',
    firstPresent(
      src.fromDate,
      src.workFromDate,
      src.wfhFromDate,
      src.wfhDate,
      src.requestDate,
      src.leaveFromDate,
      src.startDate,
    ),
  );
  assign(
    'toDate',
    firstPresent(
      src.toDate,
      src.workToDate,
      src.wfhToDate,
      src.leaveToDate,
      src.endDate,
    ),
  );
  assign(
    'totalDays',
    firstPresent(src.totalDays, src.leaveDays, src.numberOfDays, src.durationDays),
  );
  assign(
    'sessionType',
    firstPresent(src.sessionType, src.leaveSessionType, src.session),
  );
  assign(
    'sessionTypeName',
    firstPresent(src.sessionTypeName, src.sessionName, src.leaveSessionName),
  );
  assign(
    'reason',
    firstPresent(src.reason, src.leaveReason, src.wfhReason, src.remarks),
  );
  assign(
    'handoverNotes',
    firstPresent(src.handoverNotes, src.handover_notes),
  );
  assign(
    'rejectReason',
    firstPresent(src.rejectReason, src.rejectionReason, src.rejectedReason),
  );
  assign(
    'cancellationReason',
    firstPresent(
      src.cancellationReason,
      src.cancelReason,
      src.cancelledReason,
      src.canceledReason,
    ),
  );
  assign(
    'attachmentName',
    firstPresent(src.attachmentName, src.attachment),
  );
  assign('workLocation', firstPresent(src.workLocation, src.workFrom));
  assign(
    'availabilityNotes',
    firstPresent(src.availabilityNotes, src.availability, src.notes),
  );
  assign(
    'permissionDate',
    firstPresent(src.permissionDate, src.requestDate),
  );
  assign('fromTime', firstPresent(src.fromTime, src.startTime));
  assign('toTime', firstPresent(src.toTime, src.endTime));
  assign('durationMinutes', src.durationMinutes);
  assign(
    'permissionTypeName',
    firstPresent(src.permissionTypeName, src.permissionType),
  );
  assign('requestType', src.requestType);
  assign('contactInformation', src.contactInformation);
  assign('remarks', src.remarks);
  assign('attendanceDate', firstPresent(src.attendanceDate, src.requestDate));
  assign(
    'correctionRequestId',
    firstPresent(
      src.correctionRequestId,
      src.attendanceCorrectionRequestId,
      src.entityId,
    ),
  );
  assign('correctionMode', src.correctionMode);
  assign(
    'checkInTime',
    firstPresent(src.checkInTime, src.requestedCheckInTime),
  );
  assign(
    'checkOutTime',
    firstPresent(src.checkOutTime, src.requestedCheckOutTime),
  );
  assign(
    'breakStartTime',
    firstPresent(src.breakStartTime, src.requestedBreakStartTime),
  );
  assign(
    'breakEndTime',
    firstPresent(src.breakEndTime, src.requestedBreakEndTime),
  );
  assign('existingCheckInTime', src.existingCheckInTime);
  assign('existingCheckOutTime', src.existingCheckOutTime);
  assign('existingBreakStartTime', src.existingBreakStartTime);
  assign('existingBreakEndTime', src.existingBreakEndTime);
  assign('checkInSubmitted', src.checkInSubmitted);
  assign('checkOutSubmitted', src.checkOutSubmitted);
  assign('breakStartSubmitted', src.breakStartSubmitted);
  assign('breakEndSubmitted', src.breakEndSubmitted);
  assign('checkInSource', firstPresent(src.checkInSource, src.checkInAttendanceSource));
  assign('checkInOriginalSource', src.checkInOriginalSource);
  assign('checkInIsManuallyEdited', src.checkInIsManuallyEdited);
  assign('checkInKioskId', src.checkInKioskId);
  assign('checkInFaceVerified', src.checkInFaceVerified);
  assign(
    'checkInFaceVerificationLogId',
    src.checkInFaceVerificationLogId,
  );
  assign('checkInDeviceIdentifier', src.checkInDeviceIdentifier);
  assign('checkInEditedByName', src.checkInEditedByName);
  assign('checkInEditedAt', src.checkInEditedAt);
  assign('checkInEditReason', src.checkInEditReason);
  assign('checkOutSource', firstPresent(src.checkOutSource, src.checkOutAttendanceSource));
  assign('checkOutOriginalSource', src.checkOutOriginalSource);
  assign('checkOutIsManuallyEdited', src.checkOutIsManuallyEdited);
  assign('checkOutKioskId', src.checkOutKioskId);
  assign('checkOutFaceVerified', src.checkOutFaceVerified);
  assign(
    'checkOutFaceVerificationLogId',
    src.checkOutFaceVerificationLogId,
  );
  assign('checkOutDeviceIdentifier', src.checkOutDeviceIdentifier);
  assign('checkOutEditedByName', src.checkOutEditedByName);
  assign('checkOutEditedAt', src.checkOutEditedAt);
  assign('checkOutEditReason', src.checkOutEditReason);
  assign('editedByName', firstPresent(src.editedByName, src.requesterName));
  assign('editedAt', firstPresent(src.editedAt, src.submittedAt));
  assign('requesterName', src.requesterName);
  {
    const rawBreaks = firstPresent(src.breaks, src.requestedBreaks);
    if (typeof rawBreaks === 'string') {
      const trimmed = rawBreaks.trim();
      if (trimmed.startsWith('[')) {
        try {
          assign('breaks', JSON.parse(trimmed));
        } catch {
          assign('breaks', rawBreaks);
        }
      } else {
        assign('breaks', rawBreaks);
      }
    } else {
      assign('breaks', rawBreaks);
    }
  }
  // Recorded breaks the correction removes (array, or JSON text).
  assign('removedBreaks', src.removedBreaks);
  assign('requestedCheckInTime', src.requestedCheckInTime);
  assign('requestedCheckOutTime', src.requestedCheckOutTime);
  assign('requestedBreakStartTime', src.requestedBreakStartTime);
  assign('requestedBreakEndTime', src.requestedBreakEndTime);

  return Object.keys(details).length ? details : null;
}

function normalizeDetails(
  workflow: ObjectValue,
  steps: ObjectValue[],
): WorkflowDetails | null {
  const source = camelizeRow(workflow);
  const base = normalizeInboxItem(source);
  if (!base) return null;

  const normalizedSteps = steps.map(step => {
    const camel = camelizeRow(step as ObjectValue);
    return {
      stepId: numberOrNull(
        camel.workflowStepId ?? camel.stepId ?? step.workflowStepId ?? step.stepId,
      ),
      stepName: textOrNull(
        camel.stepName ??
          camel.workflowStepName ??
          step.stepName ??
          step.workflowStepName,
      ),
      stepNumber: numberOrNull(
        camel.stepNumber ??
          camel.currentStepNumber ??
          step.stepNumber ??
          step.currentStepNumber,
      ),
      stepStatus: textOrNull(
        camel.stepStatus ??
          camel.statusCode ??
          camel.status ??
          step.stepStatus ??
          step.statusCode ??
          step.status,
      ),
      approverName: textOrNull(
        camel.approverName ??
          camel.approverDisplayName ??
          step.approver_name ??
          step.approverName,
      ),
      approverEmployeeNumber: textOrNull(
        camel.approverEmployeeNumber ??
          camel.approverEmployeeCode ??
          step.approver_employee_number ??
          step.approverEmployeeNumber,
      ),
    };
  });

  const pendingStep =
    normalizedSteps.find(
      step => (step.stepStatus || '').toUpperCase() === 'PENDING',
    ) ??
    normalizedSteps[0] ??
    null;

  const requestDetails = pickRequestDetails(source);
  const rejectReasonFromSteps = [...steps]
    .reverse()
    .map(step => {
      const status = textOrNull(
        step.stepStatus ?? step.statusCode ?? step.status,
      )?.toUpperCase();
      const action = textOrNull(
        step.actionType ?? step.actionCode ?? step.action,
      )?.toUpperCase();
      if (status !== 'REJECTED' && action !== 'REJECTED') return null;
      return textOrNull(
        step.actionComments ?? step.comments ?? step.remark,
      );
    })
    .find((value): value is string => Boolean(value));

  const rejectReason =
    textOrNull(
      source.rejectReason ?? source.rejectionReason ?? source.rejectedReason,
    ) ??
    textOrNull(requestDetails?.rejectReason) ??
    rejectReasonFromSteps ??
    base.rejectReason;

  const cancellationReasonFromSteps = [...steps]
    .reverse()
    .map(step => {
      const status = textOrNull(
        step.stepStatus ?? step.statusCode ?? step.status,
      )?.toUpperCase();
      const action = textOrNull(
        step.actionType ?? step.actionCode ?? step.action,
      )?.toUpperCase();
      if (
        status !== 'CANCELLED' &&
        status !== 'CANCELED' &&
        action !== 'CANCELLED' &&
        action !== 'CANCELED'
      ) {
        return null;
      }
      return textOrNull(step.actionComments ?? step.comments ?? step.remark);
    })
    .find((value): value is string => Boolean(value));

  const cancellationReason =
    textOrNull(
      source.cancellationReason ??
        source.cancelReason ??
        source.cancelledReason ??
        source.canceledReason,
    ) ??
    textOrNull(requestDetails?.cancellationReason) ??
    cancellationReasonFromSteps ??
    base.cancellationReason;

  const rejectedAt = textOrNull(
    source.rejectedAt ?? source.rejectionDate ?? source.rejectedOn,
  );

  return {
    workflowInstanceId: base.workflowInstanceId,
    requestTypeCode: base.requestTypeCode,
    requestTypeLabel: base.requestTypeLabel,
    requestReferenceId: base.requestReferenceId,
    requestNumber: base.requestNumber,
    employee: {
      ...base.employee,
      departmentName: textOrNull(source.departmentName),
      designationName: textOrNull(source.designationName),
    },
    workflowStatusCode: base.workflowStatusCode,
    workflowStatusLabel: base.workflowStatusLabel,
    submittedAt: base.submittedAt,
    completedAt: textOrNull(source.completedAt ?? source.completedOn),
    rejectReason,
    rejectedAt,
    cancellationReason,
    currentStep: pendingStep
      ? {
          stepId: pendingStep.stepId,
          stepName: pendingStep.stepName ?? base.currentStepName,
          stepNumber: pendingStep.stepNumber,
          approverName: pendingStep.approverName,
          approverEmployeeNumber: pendingStep.approverEmployeeNumber,
        }
      : base.currentStepName
      ? {
          stepId: null,
          stepName: base.currentStepName,
          stepNumber: numberOrNull(source.currentStepNumber),
          approverName: textOrNull(
            source.currentApproverName ?? source.approverName,
          ),
          approverEmployeeNumber: textOrNull(
            source.currentApproverEmployeeNumber ??
              source.approverEmployeeNumber,
          ),
        }
      : null,
    steps: normalizedSteps,
    requestDetails,
    canApprove: false,
    canReject: false,
    canCancel: false,
  };
}

function normalizeHistoryItem(row: ObjectValue): WorkflowHistoryItem | null {
  const workflowHistoryId =
    numberOrNull(
      row.workflowActionId ?? row.workflowHistoryId ?? row.actionId,
    ) ??
    textOrNull(row.workflowActionId ?? row.workflowHistoryId) ??
    null;
  if (workflowHistoryId === null) return null;

  const actionCode = textOrNull(row.actionType ?? row.actionCode ?? row.action);
  return {
    workflowHistoryId,
    stepName: textOrNull(row.stepName ?? row.workflowStepName),
    actionCode,
    actionLabel: getWorkflowActionLabel(actionCode),
    actedByName: textOrNull(
      row.actedByName ?? row.actorName ?? row.actionByName ?? row.userName,
    ),
    actionAt: textOrNull(
      row.actionAt ?? row.actedAt ?? row.createdAt ?? row.actionDate,
    ),
    comments: textOrNull(
      row.actionComments ?? row.comments ?? row.remark ?? row.reason,
    ),
  };
}

export async function getWorkflowInbox(
  options: {
    filters?: Partial<WorkflowInboxFilters>;
    page?: number;
    pageSize?: number;
  } = {},
): Promise<WorkflowInboxResult> {
  const defaults = createDefaultWorkflowFilters();
  const normalized = normalizeWorkflowFilters({
    entityType: options.filters?.entityType ?? defaults.entityType,
    status: options.filters?.status ?? defaults.status,
    page: options.page ?? 1,
    pageSize: options.pageSize ?? 20,
  });

  const params: Record<string, string | number> = {
    page: normalized.page,
    pageSize: normalized.pageSize,
  };
  if (normalized.filters.entityType) {
    params.entityType = normalized.filters.entityType;
  }
  if (normalized.filters.status && normalized.filters.status !== 'ALL') {
    params.status = normalized.filters.status;
  }

  const response = await apiClient.get<
    Envelope<{
      items?: ObjectValue[];
      pagination?: {
        page?: unknown;
        pageSize?: unknown;
        totalRecords?: unknown;
        totalPages?: unknown;
      };
    }>
  >(
    normalized.filters.status
      ? API_ENDPOINTS.WORKFLOW.APPROVALS
      : API_ENDPOINTS.WORKFLOW.PENDING_APPROVALS,
    { params },
  );

  const data = response.data.data ?? {};
  const pagination = data.pagination ?? {};
  const items = Array.isArray(data.items)
    ? data.items
        .map(normalizeInboxItem)
        .filter((item): item is WorkflowInboxItem => item !== null)
    : [];
  const totalItems = numberOrNull(pagination.totalRecords) ?? items.length;
  const pageSize = numberOrNull(pagination.pageSize) ?? normalized.pageSize;
  const totalPages =
    numberOrNull(pagination.totalPages) ??
    Math.max(1, Math.ceil(totalItems / Math.max(pageSize, 1)));

  return {
    items,
    page: numberOrNull(pagination.page) ?? normalized.page,
    pageSize,
    totalItems,
    totalPages,
  };
}

/** Pending inbox size from pagination; pageSize 1 avoids downloading rows. */
export async function getPendingApprovalCount(
  entityType?: string,
): Promise<number | null> {
  const result = await getWorkflowInbox({
    page: 1,
    pageSize: 1,
    filters: entityType ? { entityType } : {},
  });
  const count = result.totalItems;
  if (count == null || !Number.isInteger(count) || count < 0) return null;
  return count;
}

export async function getWorkflowDetails(
  workflowInstanceId: number,
): Promise<WorkflowDetails> {
  if (!Number.isInteger(workflowInstanceId) || workflowInstanceId <= 0) {
    throw Object.assign(
      new Error('workflowInstanceId must be a positive integer'),
      { code: 'VALIDATION_ERROR' },
    );
  }

  const response = await apiClient.get<
    Envelope<{ workflow?: ObjectValue; steps?: ObjectValue[] }>
  >(API_ENDPOINTS.WORKFLOW.DETAILS(workflowInstanceId));

  const data = response.data.data ?? {};
  const workflow = (data.workflow as ObjectValue | undefined) ?? {};
  const steps = Array.isArray(data.steps) ? data.steps : [];
  const details = normalizeDetails(workflow, steps);
  if (!details) {
    throw Object.assign(new Error('Approval request was not found.'), {
      code: 'WORKFLOW_NOT_FOUND',
    });
  }
  return details;
}

export async function getWorkflowHistory(
  workflowInstanceId: number,
): Promise<WorkflowHistoryItem[]> {
  if (!Number.isInteger(workflowInstanceId) || workflowInstanceId <= 0) {
    throw Object.assign(
      new Error('workflowInstanceId must be a positive integer'),
      { code: 'VALIDATION_ERROR' },
    );
  }

  const response = await apiClient.get<Envelope<{ items?: ObjectValue[] }>>(
    API_ENDPOINTS.WORKFLOW.HISTORY(workflowInstanceId),
  );
  const items = Array.isArray(response.data.data?.items)
    ? response.data.data.items
    : [];
  return items
    .map(normalizeHistoryItem)
    .filter((item): item is WorkflowHistoryItem => item !== null);
}
